import { ModalActions, ModalTitle, ModalContent, Button, Typography } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { StyledModal } from "./StyledComponents";
import { Suspense, useCallback, useId, useState } from "react";
import { SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { AddToPlaylistSkeleton } from "./skeleton/AddToPlaylistSkeleton";
import { PlaylistsListContainer } from "./playlists-list/PlaylistsListContainer";

export interface AddToPlaylistProps extends SharedMenuItemProps {
    entryIds: string[];
    categoryId?: string;
    allowCreate: boolean;
    onCloseMenu?: () => void;
    getPlaylistsUrl: string;
    addToPlaylistUrl: string;
    label: string;
}

export interface AddToPlaylistModalProps extends AddToPlaylistProps {
    isOpen: boolean;
}

export type PlaylistType = {
    name: string;
    id: string;
    /** List of entry IDs currently contained in this playlist */
    playlistEntries?: string[];
    /**
     * UI-only flag used by the modal to represent whether
     * the current selection flow considers this playlist selected.
     */
    isSelected?: boolean;
    isNew?: boolean;
};

export interface GetPlaylistsResult {
    playlists: PlaylistType[];
    additionalPlaylists: PlaylistType[];
}

export type ChangesType = {
    [key: string]: boolean;
};

/**
 * add to playlist modal
 */
const AddToPlaylistModal = (props: AddToPlaylistModalProps) => {
    const {
        isOpen,
        categoryId,
        allowCreate,
        onCloseMenu,
        onItemStateChange,
        getPlaylistsUrl,
        addToPlaylistUrl,
        label,
        entryIds,
    } = props;

    const formId = useId();
    const [formState, setFormState] = useState<{ isDirty: boolean; isSubmitting: boolean }>({
        isDirty: false,
        isSubmitting: false,
    });

    const handleCancel = () => {
        onCloseMenu?.();
    };

    const handleFormStateChange = useCallback(
        (state: { isDirty: boolean; isSubmitting: boolean }) => {
            setFormState(state);
        },
        []
    );

    return (
        <StyledModal open={isOpen}>
            <ModalTitle>
                <div>
                    <Typography variant={"h3"}>{label}</Typography>
                </div>
            </ModalTitle>
            <ModalContent>
                <ErrorBoundary fallback={<AddToPlaylistSkeleton />}>
                    <Suspense fallback={<AddToPlaylistSkeleton />}>
                        <PlaylistsListContainer
                            getPlaylistsUrl={getPlaylistsUrl}
                            entryIds={entryIds}
                            categoryId={categoryId}
                            allowCreate={allowCreate}
                            addToPlaylistUrl={addToPlaylistUrl}
                            onCloseMenu={onCloseMenu}
                            onItemStateChange={onItemStateChange}
                            formId={formId}
                            onFormStateChange={handleFormStateChange}
                        />
                    </Suspense>
                </ErrorBoundary>
            </ModalContent>
            <ModalActions>
                <Button variant={"borderless"} color={"secondary"} onClick={handleCancel}>
                    {translate("Cancel")}
                </Button>
                <Button type="submit" form={formId} disabled={!formState.isDirty} loading={formState.isSubmitting}>
                    {translate("Add")}
                </Button>
            </ModalActions>
        </StyledModal>
    );
};

export default AddToPlaylistModal;
